import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Table, Button, Modal,
  Tag, Badge, Statistic, Input, Select,
  Space, Descriptions, message, Avatar, Spin, Tabs
} from 'antd';
import {
  EyeOutlined, StopOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ReloadOutlined, ShopOutlined,
  FileTextOutlined, BankOutlined
} from '@ant-design/icons';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getDealers, getDealerStats,
  approveDealer, rejectDealer, suspendDealer,
  approveKYC, rejectKYC
} from '../../services/dealerService';
import { CARD_STYLE, GOLD_GRADIENT } from '../../constants/theme';

const { Option } = Select;
const { Search } = Input;
const { TabPane } = Tabs;

const STATUS_CONFIG = {
  pending:   { color: '#FAAD14', badge: 'warning',  label: 'Pending'   },
  active:    { color: '#52C41A', badge: 'success',  label: 'Active'    },
  rejected:  { color: '#FF4D4F', badge: 'error',    label: 'Rejected'  },
  suspended: { color: '#FF4D4F', badge: 'error',    label: 'Suspended' },
};

const KYC_CONFIG = {
  pending:  { color: '#7A8A96', label: 'Not Submitted' },
  review:   { color: '#FAAD14', label: 'Under Review'  },
  approved: { color: '#52C41A', label: 'Approved'      },
  rejected: { color: '#FF4D4F', label: 'Rejected'      },
};

export default function DealerManagement() {
  const [dealers, setDealers]         = useState([]);
  const [selected, setSelected]       = useState(null);
  const [viewModal, setViewModal]     = useState(false);
  const [actionModal, setActionModal] = useState(false);
  const [kycModal, setKycModal]       = useState(false);
  const [actionType, setActionType]   = useState('');
  const [kycAction, setKycAction]     = useState('');
  const [reason, setReason]           = useState('');
  const [searchText, setSearchText]   = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dealerList, dealerStats] = await Promise.all([
        getDealers(),
        getDealerStats(),
      ]);
      setDealers(dealerList);
      setStats(dealerStats);
    } catch {
      message.error('Failed to load dealers');
    }
    setLoading(false);
  };

  const filtered = dealers.filter(d => {
    const matchSearch = !searchText ||
      d.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
      d.shopName?.toLowerCase().includes(searchText.toLowerCase()) ||
      d.phone?.includes(searchText) ||
      d.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      d.city?.toLowerCase().includes(searchText.toLowerCase());
    const matchStatus = filterStatus === 'all' || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleAccountAction = async () => {
    if ((actionType === 'reject' || actionType === 'suspend') && !reason) {
      message.warning('Please provide a reason');
      return;
    }
    setActionLoading(true);
    try {
      let result;
      if (actionType === 'approve')  result = await approveDealer(selected.id, 'nksapcolouracademy@gmail.com');
      if (actionType === 'reject')   result = await rejectDealer(selected.id, reason, 'nksapcolouracademy@gmail.com');
      if (actionType === 'suspend')  result = await suspendDealer(selected.id, reason, 'nksapcolouracademy@gmail.com');
      if (actionType === 'reactivate') result = await approveDealer(selected.id, 'nksapcolouracademy@gmail.com');

      if (result?.success) {
        await addDoc(collection(db, 'auditLogs'), {
          action: 'DEALER ' + actionType.toUpperCase(),
          details: `Dealer: ${selected.shopName} | ${selected.city}${reason ? ' | Reason: ' + reason : ''}`,
          adminEmail: 'nksapcolouracademy@gmail.com',
          targetId: selected.id, targetType: 'dealer',
          timestamp: serverTimestamp(),
        });
        message.success(`Dealer ${actionType}d successfully`);
        setActionModal(false);
        setViewModal(false);
        setReason('');
        loadData();
      } else {
        message.error('Action failed');
      }
    } catch {
      message.error('Action failed');
    }
    setActionLoading(false);
  };

  const handleKYCAction = async () => {
    if (kycAction === 'reject' && !reason) {
      message.warning('Please provide rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      const result = kycAction === 'approve'
        ? await approveKYC(selected.id, 'nksapcolouracademy@gmail.com')
        : await rejectKYC(selected.id, reason, 'nksapcolouracademy@gmail.com');

      if (result?.success) {
        await addDoc(collection(db, 'auditLogs'), {
          action: 'DEALER KYC ' + kycAction.toUpperCase(),
          details: `Dealer: ${selected.shopName} | Phone: ${selected.phone}${reason ? ' | Reason: ' + reason : ''}`,
          adminEmail: 'nksapcolouracademy@gmail.com',
          targetId: selected.id, targetType: 'dealer_kyc',
          timestamp: serverTimestamp(),
        });
        message.success(`KYC ${kycAction}d successfully`);
        setKycModal(false);
        setReason('');
        loadData();
      }
    } catch {
      message.error('KYC action failed');
    }
    setActionLoading(false);
  };

  const formatDate = (ts) => {
    if (!ts?.seconds) return 'N/A';
    return new Date(ts.seconds * 1000).toLocaleDateString('en-IN');
  };

  const columns = [
    {
      title: 'Dealer',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            src={r.profilePhoto || null}
            style={{ background: '#C9A84C30', color: '#C9A84C', flexShrink: 0 }}
            size={36}>
            {!r.profilePhoto && r.fullName?.charAt(0)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500, color: '#D4DCE3' }}>{r.shopName}</div>
            <div style={{ fontSize: 11, color: '#7A8A96' }}>{r.fullName} • {r.city}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      render: (_, r) => (
        <div>
          <div style={{ color: '#B0B8C1', fontSize: 12 }}>{r.phone}</div>
          <div style={{ color: '#7A8A96', fontSize: 11 }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (c) => <span style={{ color: '#7A8A96', fontSize: 12 }}>{c || '—'}</span>,
    },
    {
      title: 'KYC',
      dataIndex: 'kycStatus',
      render: (k) => {
        const cfg = KYC_CONFIG[k] || KYC_CONFIG.pending;
        return (
          <Tag style={{
            background: cfg.color + '20', color: cfg.color,
            border: '0.5px solid ' + cfg.color + '50',
            borderRadius: 20, fontSize: 11
          }}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Joined',
      render: (_, r) => (
        <span style={{ color: '#7A8A96', fontSize: 12 }}>
          {formatDate(r.createdAt)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
        return (
          <Badge status={cfg.badge}
            text={<span style={{ color: cfg.color, fontSize: 12 }}>{cfg.label}</span>} />
        );
      },
    },
    {
      title: 'Actions',
      render: (_, r) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} style={{ color: '#C9A84C' }}
            onClick={() => { setSelected(r); setViewModal(true); }} />
          {r.status === 'pending' && (
            <>
              <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
                onClick={() => { setSelected(r); setActionType('approve'); setActionModal(true); }} />
              <Button type="text" icon={<CloseCircleOutlined />} style={{ color: '#FF4D4F' }}
                onClick={() => { setSelected(r); setActionType('reject'); setActionModal(true); }} />
            </>
          )}
          {r.status === 'active' && (
            <Button type="text" icon={<StopOutlined />} style={{ color: '#FF4D4F' }}
              onClick={() => { setSelected(r); setActionType('suspend'); setActionModal(true); }} />
          )}
          {(r.status === 'suspended' || r.status === 'rejected') && (
            <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52C41A' }}
              onClick={() => { setSelected(r); setActionType('reactivate'); setActionModal(true); }} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { title: 'Total Dealers',   value: stats.total     || 0, color: '#C9A84C' },
          { title: 'Pending Approval',value: stats.pending   || 0, color: '#FAAD14' },
          { title: 'Active Dealers',  value: stats.active    || 0, color: '#52C41A' },
          { title: 'KYC Pending',     value: stats.kycPending|| 0, color: '#1890FF' },
        ].map((stat, i) => (
          <Col xs={12} lg={6} key={i}>
            <Card style={CARD_STYLE} bodyStyle={{ padding: 20 }}>
              <Statistic
                title={<span style={{ color: '#7A8A96', fontSize: 12 }}>{stat.title}</span>}
                value={stat.value}
                valueStyle={{ color: stat.color, fontSize: 28, fontWeight: 600 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card style={{ ...CARD_STYLE, marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Search
            placeholder="Search dealer, shop, phone, city..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 280 }}
          />
          <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 160 }}>
            <Option value="all">All Status</Option>
            <Option value="pending">Pending</Option>
            <Option value="active">Active</Option>
            <Option value="rejected">Rejected</Option>
            <Option value="suspended">Suspended</Option>
          </Select>
          <Button type="text" icon={<ReloadOutlined />}
            style={{ color: '#C9A84C' }} onClick={loadData}>
            Refresh
          </Button>
          <span style={{ marginLeft: 'auto', color: '#7A8A96', fontSize: 12 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${dealers.length} dealers`}
          </span>
        </div>
      </Card>

      {/* Table */}
      <Card style={CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Spin>
      </Card>

      {/* View Modal */}
      <Modal
        title={<span style={{ color: '#C9A84C' }}>
          <ShopOutlined style={{ marginRight: 8 }} />
          {selected?.shopName}
        </span>}
        open={viewModal}
        onCancel={() => setViewModal(false)}
        width={700}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setViewModal(false)}>Close</Button>
            {selected?.kycStatus === 'review' && (
              <>
                <Button danger icon={<CloseCircleOutlined />}
                  onClick={() => { setKycAction('reject'); setKycModal(true); }}>
                  Reject KYC
                </Button>
                <Button icon={<CheckCircleOutlined />}
                  style={{ background: '#52C41A', border: 'none', color: '#fff' }}
                  onClick={() => { setKycAction('approve'); setKycModal(true); }}>
                  Approve KYC
                </Button>
              </>
            )}
            {selected?.status === 'pending' && (
              <>
                <Button danger icon={<CloseCircleOutlined />}
                  onClick={() => { setActionType('reject'); setActionModal(true); }}>
                  Reject
                </Button>
                <Button icon={<CheckCircleOutlined />}
                  style={{ background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }}
                  onClick={() => { setActionType('approve'); setActionModal(true); }}>
                  Approve Dealer
                </Button>
              </>
            )}
          </div>
        }
      >
        {selected && (
          <Tabs defaultActiveKey="profile">
            <TabPane tab={<span><ShopOutlined /> Profile</span>} key="profile">
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Full Name">{selected.fullName}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selected.phone}</Descriptions.Item>
                <Descriptions.Item label="Email" span={2}>{selected.email}</Descriptions.Item>
                <Descriptions.Item label="Shop Name" span={2}>{selected.shopName}</Descriptions.Item>
                <Descriptions.Item label="Shop Address" span={2}>{selected.shopAddress}</Descriptions.Item>
                <Descriptions.Item label="City">{selected.city}</Descriptions.Item>
                <Descriptions.Item label="Pincode">{selected.pincode}</Descriptions.Item>
                <Descriptions.Item label="GST Number">{selected.gstNumber || '—'}</Descriptions.Item>
                <Descriptions.Item label="Business Type">{selected.businessType}</Descriptions.Item>
                <Descriptions.Item label="Category" span={2}>{selected.category}</Descriptions.Item>
                <Descriptions.Item label="Account Status">
                  <Badge
                    status={(STATUS_CONFIG[selected.status] || STATUS_CONFIG.pending).badge}
                    text={selected.status}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Joined">
                  {formatDate(selected.createdAt)}
                </Descriptions.Item>
                {selected.rejectReason && (
                  <Descriptions.Item label="Reject Reason" span={2}>
                    <span style={{ color: '#FF4D4F' }}>{selected.rejectReason}</span>
                  </Descriptions.Item>
                )}
                {selected.suspendReason && (
                  <Descriptions.Item label="Suspend Reason" span={2}>
                    <span style={{ color: '#FF4D4F' }}>{selected.suspendReason}</span>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </TabPane>

            <TabPane tab={<span><FileTextOutlined /> KYC Documents</span>} key="kyc">
              <div style={{ marginBottom: 12 }}>
                <Tag style={{
                  background: (KYC_CONFIG[selected.kycStatus]?.color || '#7A8A96') + '20',
                  color: KYC_CONFIG[selected.kycStatus]?.color || '#7A8A96',
                  border: 'none', borderRadius: 20, padding: '4px 12px', fontSize: 13
                }}>
                  KYC Status: {KYC_CONFIG[selected.kycStatus]?.label || 'Not Submitted'}
                </Tag>
              </div>
              {selected.kyc ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {Object.entries(selected.kyc).map(([key, url]) => url && (
                    <div key={key} style={{
                      padding: '12px 16px',
                      background: 'rgba(201,168,76,0.06)',
                      border: '0.5px solid rgba(201,168,76,0.2)',
                      borderRadius: 8
                    }}>
                      <div style={{ color: '#C9A84C', fontSize: 12, fontWeight: 500, marginBottom: 8, textTransform: 'capitalize' }}>
                        {key.replace(/_/g, ' ')}
                      </div>
                      {url.includes('.pdf') ? (
                        <a href={url} target="_blank" rel="noreferrer"
                          style={{ color: '#1890FF' }}>View PDF Document</a>
                      ) : (
                        <img src={url} alt={key}
                          style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 6 }}
                          onError={e => e.target.style.display = 'none'}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#7A8A96', textAlign: 'center', padding: '24px 0' }}>
                  No KYC documents submitted yet
                </div>
              )}
            </TabPane>

            <TabPane tab={<span><BankOutlined /> Bank Details</span>} key="bank">
              {selected.bankDetails ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Account Holder">
                    {selected.bankDetails.accountName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bank Name">
                    {selected.bankDetails.bankName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account Number">
                    {selected.bankDetails.accountNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="IFSC Code">
                    {selected.bankDetails.ifsc}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <div style={{ color: '#7A8A96', textAlign: 'center', padding: '24px 0' }}>
                  No bank details submitted yet
                </div>
              )}
            </TabPane>
          </Tabs>
        )}
      </Modal>

      {/* Account Action Modal */}
      <Modal
        title={
          <span style={{ color: actionType === 'approve' || actionType === 'reactivate' ? '#52C41A' : '#FF4D4F' }}>
            {actionType === 'approve'    && 'Approve Dealer'}
            {actionType === 'reject'     && 'Reject Dealer'}
            {actionType === 'suspend'    && 'Suspend Dealer'}
            {actionType === 'reactivate' && 'Reactivate Dealer'}
            {selected && ` — ${selected.shopName}`}
          </span>
        }
        open={actionModal}
        onCancel={() => { setActionModal(false); setReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setActionModal(false); setReason(''); }}>Cancel</Button>
            <Button
              loading={actionLoading}
              danger={actionType === 'reject' || actionType === 'suspend'}
              style={
                (actionType === 'approve' || actionType === 'reactivate')
                  ? { background: GOLD_GRADIENT, border: 'none', color: '#0D0D0D', fontWeight: 600 }
                  : {}
              }
              onClick={handleAccountAction}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div style={{ padding: '12px 0', color: '#B0B8C1', fontSize: 13 }}>
          {actionType === 'approve'    && 'Dealer will be activated and can start listing products.'}
          {actionType === 'reject'     && 'Dealer registration will be rejected. They will be notified.'}
          {actionType === 'suspend'    && 'Dealer account will be suspended immediately.'}
          {actionType === 'reactivate' && 'Dealer account will be reactivated.'}
        </div>
        <div style={{
          padding: '12px 16px', marginBottom: 16,
          background: 'rgba(201,168,76,0.08)',
          border: '0.5px solid rgba(201,168,76,0.2)',
          borderRadius: 8
        }}>
          <div style={{ color: '#D4DCE3', fontWeight: 500 }}>{selected?.shopName}</div>
          <div style={{ color: '#7A8A96', fontSize: 12, marginTop: 4 }}>
            {selected?.fullName} • {selected?.city} • {selected?.phone}
          </div>
        </div>
        {(actionType === 'reject' || actionType === 'suspend') && (
          <Input.TextArea
            rows={3}
            placeholder="Required: Reason for this action..."
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{
              background: '#222222',
              border: '0.5px solid rgba(201,168,76,0.3)',
              color: '#D4DCE3'
            }}
          />
        )}
      </Modal>

      {/* KYC Action Modal */}
      <Modal
        title={
          <span style={{ color: kycAction === 'approve' ? '#52C41A' : '#FF4D4F' }}>
            {kycAction === 'approve' ? 'Approve KYC' : 'Reject KYC'} — {selected?.shopName}
          </span>
        }
        open={kycModal}
        onCancel={() => { setKycModal(false); setReason(''); }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => { setKycModal(false); setReason(''); }}>Cancel</Button>
            <Button
              loading={actionLoading}
              danger={kycAction === 'reject'}
              style={kycAction === 'approve'
                ? { background: '#52C41A', border: 'none', color: '#fff' }
                : {}}
              onClick={handleKYCAction}
            >
              Confirm {kycAction === 'approve' ? 'KYC Approval' : 'KYC Rejection'}
            </Button>
          </div>
        }
      >
        <div style={{ padding: '8px 0 16px', color: '#B0B8C1', fontSize: 13 }}>
          {kycAction === 'approve'
            ? 'KYC documents verified. Dealer will be notified of approval.'
            : 'KYC documents rejected. Please provide reason so dealer can resubmit.'}
        </div>
        {kycAction === 'reject' && (
          <Input.TextArea
            rows={3}
            placeholder="Required: Reason for KYC rejection..."
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{
              background: '#222222',
              border: '0.5px solid rgba(201,168,76,0.3)',
              color: '#D4DCE3'
            }}
          />
        )}
      </Modal>
    </div>
  );
}